'use client';

import { useGomokuGame } from '../hooks/useGomokuGame';
import Board from './Board';
import GameControls from './GameControls';
import GameOverModal from './GameOverModal';
import { useState, useEffect } from 'react';

export const GomokuGame: React.FC = () => {
  const {
    board,
    currentPlayer,
    winner,
    gameOver,
    lastMove,
    moveCount,
    settings,
    isAITurn,
    aiIsThinking,
    placeStone,
    resetGame,
    updateSettings,
  } = useGomokuGame();
  
  const [showModal, setShowModal] = useState(false);
  
  useEffect(() => {
    // Delay showing the modal when game is over for better UX
    if (gameOver) {
      const timer = setTimeout(() => setShowModal(true), 1000);
      return () => clearTimeout(timer);
    } else {
      setShowModal(false);
    }
  }, [gameOver]);
  
  const handleNewGame = () => {
    resetGame();
  };
  
  // Allow closing the modal without starting a new game
  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <div className="w-full max-w-[650px] mx-auto px-4 py-6">
      <h1 className="text-3xl font-bold text-center mb-6 text-indigo-700">Gomoku</h1>
      
      <div className="mb-6">
        <Board 
          board={board}
          currentPlayer={currentPlayer}
          gameOver={gameOver}
          lastMove={lastMove}
          onPlaceStone={placeStone}
          isAITurn={isAITurn}
        />
      </div>
      
      <GameControls
        currentPlayer={currentPlayer}
        gameOver={gameOver}
        moveCount={moveCount}
        winner={winner}
        isAITurn={isAITurn}
        aiIsThinking={aiIsThinking}
        settings={settings}
        onReset={resetGame}
        onUpdateSettings={updateSettings}
      />
      
      <GameOverModal
        isVisible={showModal}
        winner={winner}
        moveCount={moveCount}
        onNewGame={handleNewGame}
        settings={settings}
      />
      
      {/* Game instructions */}
      <div className="mt-8 text-sm text-gray-600 bg-white p-4 rounded-lg shadow-sm">
        <h3 className="font-semibold mb-2">How to play:</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Players take turns placing stones on the board intersections</li>
          <li>Black plays first, followed by White</li>
          <li>First player to form an unbroken line of 5 stones (horizontally, vertically, or diagonally) wins</li>
          <li>If the board fills up with no winner, the game is a draw</li>
        </ul>
        
        <h3 className="font-semibold mt-3 mb-2">Game Modes:</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Human vs Human</strong> - Play against a friend on the same device</li>
          <li><strong>Human vs AI</strong> - Play against the computer with adjustable difficulty</li>
        </ul>
      </div>
    </div>
  );
};

export default GomokuGame;